/**
 * A passage with its gaps drawn as blanks: a dashed accent underline of a fixed
 * width (handoff §7: 40 px in a row's preview, §19.6: 56 px in the deck's reading
 * pane). Plain text in, spans in: it renders what the server found and never
 * searches the text itself (ADR-115 Decision 4).
 *
 * The gap's own words are not shown, which is the point of a preview of a
 * gap-fill text. The blank is an image with a label for assistive technology, so
 * a screen reader hears «пропуск» where the word would have been.
 */
import { useLocale } from '../../i18n/context'

interface Props {
  text: string
  /** Character ranges `[start, end)` of `text` to draw as blanks, in any order. */
  spans: readonly (readonly [number, number])[]
  blankWidth: number
  /** On each blank, for tests and measurements. */
  blankTestId?: string
}

export default function GappedText({ text, spans, blankWidth, blankTestId }: Props) {
  const { tr } = useLocale()
  const ordered = [...spans].sort((a, b) => a[0] - b[0])
  const parts: React.ReactNode[] = []
  let at = 0
  ordered.forEach(([start, end], i) => {
    // The server never overlaps gaps; a span inside the last one is skipped all the same.
    if (start < at || end > text.length) return
    if (start > at) parts.push(text.slice(at, start))
    parts.push(
      <span
        key={i}
        role="img"
        aria-label={tr.textList.gapBlank}
        data-testid={blankTestId}
        style={{
          display: 'inline-block', width: blankWidth, height: '1em', verticalAlign: 'baseline',
          borderBottom: '1.5px dashed var(--accent)', margin: '0 2px',
        }}
      />,
    )
    at = end
  })
  parts.push(text.slice(at))
  return <>{parts}</>
}
